import { NoteEvent, TempoEvent, TimeSigEvent } from './midi-parser';
import { NoteGroup } from '../machines/gameMachine';
import { midiToCoord } from './note-colors';
import { D_REF_MIDI } from './keyboard-layouts';

const CHORD_THRESHOLD_MS = 20;

export type GameNoteGroup = NoteGroup;
export function midiToCellId(midiNote: number): string {
  const [x, y] = midiToCoord(midiNote);
  return `${x}_${y}`;
}
export function buildNoteGroups(events: NoteEvent[], maxKeys = 8): GameNoteGroup[] {
  const sorted = [...events]
    .filter(e => e.channel !== 9)
    .sort((a, b) => a.startMs - b.startMs);

  const groups: GameNoteGroup[] = [];

  for (const event of sorted) {
    const last = groups[groups.length - 1];
    if (last && event.startMs - last.startMs <= CHORD_THRESHOLD_MS) {
      const cellId = midiToCellId(event.midiNote);
      if (!last.cellIds.includes(cellId) && last.cellIds.length < maxKeys) {
        last.cellIds.push(cellId);
        last.midiNotes.push(event.midiNote);
      }
    } else {
      groups.push({
        startMs: event.startMs,
        cellIds: [midiToCellId(event.midiNote)],
        midiNotes: [event.midiNote],
      });
    }
  }

  return groups;
}
export function computeMedianMidiNote(events: NoteEvent[]): number {
  if (events.length === 0) return D_REF_MIDI;
  const sorted = [...events].map(e => e.midiNote).sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}
export function transposeSong(groups: GameNoteGroup[], semitones: number): GameNoteGroup[] {
  return groups.map(group => {
    const transposedMidi = group.midiNotes.map(m => m + semitones);
    const newCellIds: string[] = [];
    const newMidiNotes: number[] = [];
    for (let i = 0; i < transposedMidi.length; i++) {
      const midi = transposedMidi[i];
      if (midi >= 0 && midi <= 127) {
        newCellIds.push(midiToCellId(midi));
        newMidiNotes.push(midi);
      }
    }
    return { startMs: group.startMs, cellIds: newCellIds, midiNotes: newMidiNotes };
  }).filter(g => g.cellIds.length > 0);
}
export function cropToRange(groups: GameNoteGroup[], availableRange: ReadonlySet<string>): GameNoteGroup[] {
  return groups.map(group => {
    const filtered = group.cellIds.reduce<{ cellIds: string[]; midiNotes: number[] }>(
      (acc, cellId, i) => {
        if (availableRange.has(cellId)) {
          acc.cellIds.push(cellId);
          acc.midiNotes.push(group.midiNotes[i]);
        }
        return acc;
      },
      { cellIds: [], midiNotes: [] },
    );
    return { startMs: group.startMs, cellIds: filtered.cellIds, midiNotes: filtered.midiNotes };
  }).filter(g => g.cellIds.length > 0);
}
export function foldOctaves(groups: GameNoteGroup[], availableRange: ReadonlySet<string>): GameNoteGroup[] {
  return groups.map(group => {
    const foldedCellIds: string[] = [];
    const foldedMidiNotes: number[] = [];
    for (let i = 0; i < group.cellIds.length; i++) {
      const cellId = group.cellIds[i];
      const midi = group.midiNotes[i];
      if (availableRange.has(cellId)) {
        foldedCellIds.push(cellId);
        foldedMidiNotes.push(midi);
        continue;
      }
      let found = false;
      for (let octShift = 1; octShift <= 4; octShift++) {
        for (const dir of [1, -1]) {
          const shifted = midi + dir * octShift * 12;
          const [sx, sy] = midiToCoord(shifted);
          const sCellId = `${sx}_${sy}`;
          if (availableRange.has(sCellId)) {
            foldedCellIds.push(sCellId);
            foldedMidiNotes.push(shifted);
            found = true;
            break;
          }
        }
        if (found) break;
      }
    }
    return { startMs: group.startMs, cellIds: foldedCellIds, midiNotes: foldedMidiNotes };
  }).filter(g => g.cellIds.length > 0);
}
export function findOptimalTransposition(groups: GameNoteGroup[], availableRange: ReadonlySet<string>): number {
  let bestSemitones = 0;
  let bestCount = -1;

  for (let semitones = -24; semitones <= 24; semitones++) {
    const transposed = transposeSong(groups, semitones);
    let count = 0;
    for (const g of transposed) {
      for (const cellId of g.cellIds) {
        if (availableRange.has(cellId)) count++;
      }
    }
    if (count > bestCount || (count === bestCount && Math.abs(semitones) < Math.abs(bestSemitones))) {
      bestCount = count;
      bestSemitones = semitones;
    }
  }

  return bestSemitones;
}
export type QuantizationLevel = '1/4' | '1/8' | '1/16' | 'none';

const QUANTIZE_DIVISOR: Record<Exclude<QuantizationLevel, 'none'>, number> = {
  '1/4': 1,
  '1/8': 2,
  '1/16': 4,
};
function tickPositionToMs(tempoMap: TempoEvent[], tickPosition: number, ppq: number): number {
  if (tempoMap.length === 0) return 0;
  let ms = 0;
  let prevTick = 0;
  let usPerQuarter = 500000;

  for (const entry of tempoMap) {
    if (entry.tickPosition >= tickPosition) break;
    ms += ((entry.tickPosition - prevTick) * usPerQuarter) / ppq / 1000;
    prevTick = entry.tickPosition;
    usPerQuarter = entry.microsecondsPerQuarter;
  }

  ms += ((tickPosition - prevTick) * usPerQuarter) / ppq / 1000;
  return ms;
}
function buildBeatGrid(
  tempoMap: TempoEvent[],
  timeSigMap: TimeSigEvent[],
  level: Exclude<QuantizationLevel, 'none'>,
  endMs: number,
): number[] {
  const divisor = QUANTIZE_DIVISOR[level];
  const grid: number[] = [];
  const ppq = timeSigMap.length > 0 ? timeSigMap[0].ticksPerQuarter : 480;

  const segmentBoundariesMs = tempoMap.map(entry =>
    tickPositionToMs(tempoMap, entry.tickPosition, ppq),
  );

  for (let i = 0; i < tempoMap.length; i++) {
    const bpm = tempoMap[i].bpm;
    const spacingMs = (60000 / bpm) / divisor;
    const segmentStartMs = segmentBoundariesMs[i] ?? 0;
    const segmentEndMs = i + 1 < tempoMap.length
      ? (segmentBoundariesMs[i + 1] ?? endMs + spacingMs)
      : endMs + spacingMs;

    let t = segmentStartMs;
    while (t < segmentEndMs && t <= endMs + spacingMs) {
      grid.push(t);
      t += spacingMs;
    }
  }

  return grid;
}
export function quantizeNotes(
  events: NoteEvent[],
  tempoMap: TempoEvent[],
  timeSigMap: TimeSigEvent[],
  level: QuantizationLevel,
): NoteEvent[] {
  if (level === 'none') return events;
  if (events.length === 0) return [];

  const lastEvent = events[events.length - 1];
  if (!lastEvent) return [];
  const endMs = lastEvent.startMs + lastEvent.durationMs;

  const grid = buildBeatGrid(tempoMap, timeSigMap, level, endMs);
  if (grid.length === 0) return events;
  const snapToGrid = (ms: number): number => {
    let lo = 0;
    let hi = grid.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (grid[mid] < ms) lo = mid + 1;
      else hi = mid;
    }
    const candidate = grid[lo];
    if (candidate === undefined) return grid[grid.length - 1] ?? ms;
    if (lo > 0) {
      const prev = grid[lo - 1];
      if (prev !== undefined && Math.abs(ms - prev) < Math.abs(ms - candidate)) return prev;
    }
    return candidate;
  };

  const result: NoteEvent[] = [];
  for (const event of events) {
    const snappedStart = snapToGrid(event.startMs);
    result.push({ ...event, startMs: snappedStart });
  }
  const seen = new Set<string>();
  const deduped: NoteEvent[] = [];
  for (const event of result) {
    const key = `${event.startMs.toFixed(2)}_${event.midiNote}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(event);
    }
  }

  deduped.sort((a, b) => a.startMs - b.startMs);
  return deduped;
}
